import { useEffect, useMemo, useRef } from 'react';
import { mdiPlaylistEdit } from '@mdi/js';
import Icon from '@mdi/react';
import { PersonAddAlt1, PersonRemoveOutlined } from '@mui/icons-material';
import {
  Avatar,
  Box,
  Checkbox,
  IconButton,
  ListItemIcon,
  MenuItem,
  Select,
  Skeleton,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { UseMutationResult } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import { JunoUser, RoleEnum, Site, UserDeactivationReasonEnum } from '@juno/client-api/model';
import { BasicKebobMenu } from '@juno/ui';

const uniqueId = uuidv4();

export const searchStyles = {
  bgcolor: 'white',
  borderRadius: 2,
  '.MuiInputBase-root': {
    borderRadius: 2,
  },
  '.MuiFilledInput-root': {
    backgroundColor: 'white',
    borderRadius: 2,
  },
  '.MuiOutlinedInput-notchedOutline': {
    '&:hover': {
      borderColor: 'transparent',
      backgroundColor: 'transparent',
    },
  },
  '&:hover': {
    borderColor: 'rgba(0,0,0,.1)',
    backgroundColor: 'rgba(0,0,0,.01)',
    '.MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0,0,0,.2)',
      backgroundColor: 'rgba(0,0,0,.01)',
    },
  },
};

export const UserListSkeleton: React.FC = () => {
  return (
    <TableRow>
      <TableCell>
        <Skeleton variant='circular' width={60} height={60} />
      </TableCell>
      <TableCell>
        <Skeleton variant='text' width={'auto'} height={40} />
      </TableCell>
      <TableCell>
        <Skeleton variant='text' width={'auto'} height={40} />
      </TableCell>
      <TableCell>
        <Skeleton variant='text' width={'auto'} height={40} />
      </TableCell>
      <TableCell>
        <Skeleton variant='rectangular' width={'auto'} height={40} sx={{ borderRadius: 1 }} />
      </TableCell>
      <TableCell>
        <Skeleton variant='circular' width={25} height={25} sx={{ ml: 1 }} />
      </TableCell>
      <TableCell>
        <Skeleton variant='circular' width={25} height={25} sx={{ ml: 1 }} />
      </TableCell>
    </TableRow>
  );
};

interface AccessibleCheckboxProps {
  selectedUsers: Set<string>;
  totalUsers: number;
  allUserIds: string[];
  onChange: (newSelectedUsers: Set<string>) => void;
}

export const AccessibleCheckbox: React.FC<AccessibleCheckboxProps> = ({
  selectedUsers,
  totalUsers,
  allUserIds,
  onChange,
}) => {
  const checkboxRef = useRef<HTMLInputElement>(null);
  const selectedSet = useMemo(() => new Set(selectedUsers), [selectedUsers]);

  const isChecked = useMemo(
    () => selectedSet.size > 0 && selectedSet.size === totalUsers,
    [selectedSet, totalUsers],
  );

  const isIndeterminate = useMemo(
    () => selectedSet.size > 0 && selectedSet.size < totalUsers,
    [selectedSet, totalUsers],
  );

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  return (
    <Checkbox
      inputRef={checkboxRef}
      checked={isChecked}
      indeterminate={isIndeterminate}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onChange(isChecked || isIndeterminate ? new Set() : new Set(allUserIds));
        }
      }}
      onChange={(e) => {
        if (e.target.checked && !isIndeterminate) {
          onChange(new Set(allUserIds));
        } else {
          onChange(new Set());
        }
      }}
      sx={{
        transition: 'all 0.2s ease-in-out',
        '& .MuiSvgIcon-root': {
          transition: 'transform 0.2s ease-in-out',
          transform: isIndeterminate ? 'scale(1.1)' : 'scale(1)',
        },
      }}
      inputProps={{
        'aria-label': isIndeterminate ? 'Partially selected users' : 'Select all users',
      }}
    />
  );
};

interface UserListItemProps {
  user: JunoUser;
  isSelected: boolean;
  isBulkSelecting: boolean;
  toggleUserSelection: (userId: string) => void;
  singleUserDisable: (userId: string) => void;
  setViewEditOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setUserToEdit: React.Dispatch<React.SetStateAction<JunoUser | null>>;
  userUpdate: UseMutationResult<any, unknown, any, unknown>;
  platformId: string;
  deactivationReason: UserDeactivationReasonEnum | undefined;
  deactivationDescription: string | undefined;
  isViewerStaff: boolean;
}

export const UserListItem: React.FC<UserListItemProps> = ({
  user,
  isSelected,
  isBulkSelecting,
  toggleUserSelection,
  setViewEditOpen,
  setUserToEdit,
  singleUserDisable,
  userUpdate,
  platformId,
  deactivationReason,
  deactivationDescription,
  isViewerStaff,
}) => {
  return (
    <Tooltip
      arrow
      title={
        user.active ? (
          ''
        ) : (
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              maxWidth: 250,
            }}
          >
            <Typography variant='body2' fontWeight='bold' sx={{ mb: 1 }}>
              User deactivated for: {deactivationReason}
            </Typography>
            {deactivationDescription && (
              <Typography variant='body2'>Admin notes: {deactivationDescription}</Typography>
            )}
          </Box>
        )
      }
      placement='bottom'
    >
      <TableRow
        role='row'
        sx={{
          cursor: 'pointer',
        }}
        selected={isSelected}
      >
        {isBulkSelecting && (
          <TableCell>
            <Checkbox checked={isSelected} onChange={() => toggleUserSelection(user.id)} />
          </TableCell>
        )}
        <TableCell>
          <Avatar
            src={user.avatar || ''}
            alt={`${user.first_name} ${user.last_name}`}
            sx={{
              width: 60,
              height: 60,
              opacity: user.active ? 1 : 0.3,
            }}
          />
        </TableCell>
        <TableCell>
          <Typography sx={{ opacity: user.active ? 1 : 0.3 }} variant='body2' color='textSecondary'>
            {user.first_name}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant='body2' color='textSecondary' sx={{ opacity: user.active ? 1 : 0.3 }}>
            {user.last_name}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant='body2' color='textSecondary' sx={{ opacity: user.active ? 1 : 0.3 }}>
            {user.email}
          </Typography>
        </TableCell>
        <TableCell>
          <Select
            role='combobox'
            aria-expanded='false'
            inputProps={{
              'aria-label': 'Role Select',
              'aria-controls': `role_select_drowpdown_menu_${uniqueId}`,
            }}
            MenuProps={{
              PaperProps: {
                id: `role_select_drowpdown_menu_${uniqueId}`,
              },
            }}
            value={user.role}
            onChange={(e) => {
              userUpdate.mutate({
                platformId,
                userId: user.id,
                data: {
                  ...user,
                  role: e.target.value as RoleEnum,
                },
              });
            }}
            size='small'
            sx={{ minWidth: 120, opacity: user.active ? 1 : 0.3 }}
            disabled={user?.role === RoleEnum.NUMBER_2}
          >
            <MenuItem value={RoleEnum.NUMBER_0}>Member</MenuItem>
            <MenuItem value={RoleEnum.NUMBER_1}>Admin</MenuItem>
            {user?.role === RoleEnum.NUMBER_2 ? (
              <MenuItem value={RoleEnum.NUMBER_2}>GrowthZone Staff</MenuItem>
            ) : null}
          </Select>
        </TableCell>
        <TableCell>
          <Tooltip
            title={user.active ? 'Deactivate User' : 'Reactivate User'}
            placement='bottom-start'
          >
            <span>
              <IconButton
                onClick={() =>
                  user.active ? singleUserDisable(user.id) : toggleUserSelection(user.id)
                }
                sx={{
                  transform: isSelected ? 'scale(1.1)' : 'scale(1)',
                  transition: 'transform 0.2s ease-in-out',
                  color: 'inherit',
                }}
                disabled={isBulkSelecting || (user?.role === RoleEnum.NUMBER_2 && !isViewerStaff)}
              >
                {user.active ? <PersonRemoveOutlined /> : <PersonAddAlt1 />}
              </IconButton>
            </span>
          </Tooltip>
        </TableCell>
        <TableCell>
          <BasicKebobMenu>
            <MenuItem
              aria-label='View/edit details'
              onClick={() => {
                setUserToEdit(user);
                setViewEditOpen(true);
              }}
            >
              <ListItemIcon>
                <Icon path={mdiPlaylistEdit} size={1} />
              </ListItemIcon>
              <Typography>{'View/Edit Details'}</Typography>
            </MenuItem>
            {/* // TODO future functionality */}
            {/* <MenuItem onClick={(e) => {}} aria-label='Download user data'>
                <ListItemIcon>
                  <Icon path={mdiTrayArrowDown} size={1} />
                </ListItemIcon>
                <Typography>Download User Data</Typography>
              </MenuItem> */}
          </BasicKebobMenu>
        </TableCell>
      </TableRow>
    </Tooltip>
  );
};
